import { pick } from 'lodash-es';

import ENDPOINTS from 'api/endpoints';
import { doDelete, doGet, doPost, doPut } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import type {
  ImportType,
  WriteMode,
} from 'components/organisms/ImportPanel/types';
import { generateNextPath } from 'lib/routeHelper';
import type {
  ImportTopicsCSVResponse,
  Topic,
  TopicsResponse,
  UpdateTopicsRequest,
  UpsertTopic,
} from './types';

export const getTopics = (accountId: string, projectId: string) => {
  return doGet<TopicsResponse>(
    generateNextPath(ENDPOINTS.TOPICS, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2' },
  );
};

export const updateTopics = ({
  accountId,
  projectId,
  update,
}: {
  accountId: string;
  projectId: string;
  update: UpdateTopicsRequest;
}) => {
  return doPost<TopicsResponse>(
    generateNextPath(ENDPOINTS.TOPICS, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      ...update,
      updated: update.updated?.map(t =>
        pick(t, 'id', 'name', 'content', 'actions', 'example_queries'),
      ),
    },
    { version: 'v2' },
  );
};

export const putTopic = ({
  accountId,
  projectId,
  topicId,
  topic,
}: {
  accountId: string;
  projectId: string;
  topicId: string;
  topic: UpsertTopic;
}) => {
  return doPut<Topic>(
    generateNextPath(ENDPOINTS.TOPIC, {
      accountId,
      projectId,
      topicId,
    }),
    LOGGER_TYPE.JUPITER,
    pick(topic, 'name', 'content', 'actions', 'example_queries'),
    { version: 'v2' },
  );
};

export const deleteTopic = ({
  accountId,
  projectId,
  topicId,
}: {
  accountId: string;
  projectId: string;
  topicId: string;
}) => {
  return doDelete<Topic>(
    generateNextPath(ENDPOINTS.TOPIC, {
      accountId,
      projectId,
      topicId,
    }),
    LOGGER_TYPE.JUPITER,
    undefined,
    { version: 'v2' },
  );
};

export const importTopics = ({
  accountId,
  projectId,
  file,
  fileType,
  writeMode,
}: {
  accountId: string;
  projectId: string;
  file: File;
  fileType: ImportType;
  writeMode: WriteMode;
}) => {
  const url = generateNextPath(
    ENDPOINTS.TOPICS_IMPORT,
    { accountId, projectId },
    {
      type: fileType,
      write_mode: writeMode,
    },
  );
  const bodyFormData = new FormData();
  const blob = new Blob([file], { type: fileType });
  bodyFormData.append(fileType, blob);

  return doPost<ImportTopicsCSVResponse>(
    url,
    LOGGER_TYPE.JUPITER,
    bodyFormData,
    {
      postingFile: true,
      version: 'v2',
      parseResponseToJSON: true,
    },
  );
};

export const importTopicsUrl = ({
  accountId,
  projectId,
  url,
  writeMode,
}: {
  accountId: string;
  projectId: string;
  url: string;
  writeMode: WriteMode;
}) => {
  return doPost<ImportTopicsCSVResponse>(
    generateNextPath(
      ENDPOINTS.TOPICS_IMPORT_URL,
      { accountId, projectId },
      { write_mode: writeMode },
    ),
    LOGGER_TYPE.JUPITER,
    { url },
    { version: 'v2', parseResponseToJSON: true },
  );
};

export const exportTopics = ({
  accountId,
  projectId,
}: {
  accountId: string;
  projectId: string;
}) => {
  return doGet<{ data: Blob; headers: Headers }>(
    generateNextPath(ENDPOINTS.TOPICS_EXPORT, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2', downloadingFile: true, returnStatusCode: true },
  );
};
